import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useThemeStore } from '../stores/themeStore';
import { useCMSStore } from '../stores/cmsStore';
import { useLookupsStore } from '../stores/lookupsStore';
import { useNotificationsStore } from '../stores/notificationsStore';
import ProfileAvatar from './ProfileAvatar';
import Notifications from './Notifications';

const TopBar = () => {
  const { user, profile, logout, accessToken, isAuthenticated, apparentRoleId, setApparentRoleId } = useAuthStore();
  const { theme, setTheme } = useThemeStore();
  const { fetchMenu, getMenu, currentMenuType, setCurrentMenuType } = useCMSStore();
  const { fetchEntries } = useLookupsStore();
  const { fetchUnreadCount } = useNotificationsStore();
  const [menuItems, setMenuItems] = useState([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [roles, setRoles] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  const isAdmin = user?.is_admin === 1;
  const isAdminMode = currentMenuType === 'ADMIN_TOP_BAR';

  // Fetch unread notification count periodically
  useEffect(() => {
    if (isAuthenticated && accessToken) {
      // Fetch immediately
      fetchUnreadCount(accessToken);

      // Then fetch every 30 seconds
      const interval = setInterval(() => {
        fetchUnreadCount(accessToken);
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [isAuthenticated, accessToken, fetchUnreadCount]);

  const handleLogout = async () => {
    // Reset to normal mode before logout
    if (isAdminMode) {
      setCurrentMenuType('TOP_BAR');
    }

    // Call logout
    await logout();

    // Navigate to home page
    navigate('/');
  };

  // Ensure menu type is correct for user's admin status
  useEffect(() => {
    // Non-admin users should always see TOP_BAR
    if (!isAdmin && currentMenuType !== 'TOP_BAR') {
      setCurrentMenuType('TOP_BAR');
    }
  }, [isAdmin, currentMenuType, setCurrentMenuType]);

  useEffect(() => {
    // Fetch menu based on current menu type and authentication state
    const loadMenu = async () => {
      const result = await fetchMenu(currentMenuType, accessToken, null, apparentRoleId);
      if (result.success) {
        setMenuItems(result.items || []);
      }
    };

    loadMenu();
  }, [currentMenuType, accessToken, isAuthenticated, apparentRoleId, fetchMenu]);

  // Fetch roles for admin role dropdown
  useEffect(() => {
    if (isAdmin && accessToken) {
      const loadRoles = async () => {
        const result = await fetchEntries('lk_role', accessToken);
        if (result.success) {
          setRoles(result.entries || []);
        }
      };
      loadRoles();
    }
  }, [isAdmin, accessToken, fetchEntries]);

  const cycleTheme = () => {
    if (theme === 'system') setTheme('light');
    else if (theme === 'light') setTheme('dark');
    else setTheme('system');
  };

  const getThemeIcon = () => {
    if (theme === 'system') {
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      );
    }
    if (theme === 'light') {
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      );
    }
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
      </svg>
    );
  };

  const toggleAdminMode = async () => {
    const newMenuType = isAdminMode ? 'TOP_BAR' : 'ADMIN_TOP_BAR';
    setCurrentMenuType(newMenuType);

    // Fetch the new menu to get the first item
    const result = await fetchMenu(newMenuType, accessToken, null, apparentRoleId);
    if (result.success && result.items && result.items.length > 0) {
      // Navigate to the first menu item (which will be represented by the logo)
      const firstItem = result.items[0];
      const link = getMenuItemLink(firstItem);
      navigate(link);
    }
  };

  const handleRoleChange = (e) => {
    const roleId = e.target.value ? parseInt(e.target.value) : null;
    setApparentRoleId(roleId);
    // Refresh the current page to apply new role filtering
    window.location.reload();
  };

  // Get the effective role for display
  const getEffectiveRole = () => {
    if (apparentRoleId) {
      return roles.find(r => r.id === apparentRoleId);
    }
    return roles.find(r => r.id === profile?.role_id);
  };

  const getMenuItemLink = (item) => {
    if (item.page_slug) {
      // Home page goes to root, others to /slug
      return item.page_slug === 'home' ? '/' : `/${item.page_slug}`;
    }
    if (item.route_code) {
      return `/${item.route_code}`;
    }
    if (item.url) {
      return item.url;
    }
    return '#';
  };

  const handleMenuClick = (e, item) => {
    if (item.url) {
      // External URL - let the browser handle it
      window.open(item.url, '_blank');
      e.preventDefault();
    }
  };

  const isMenuItemActive = (item) => {
    const link = getMenuItemLink(item);
    // Check if current path matches the menu item link
    return location.pathname === link;
  };

  const isLogoActive = () => {
    // Logo represents the first menu item
    if (menuItems.length === 0) return false;
    const firstItem = menuItems[0];
    const link = getMenuItemLink(firstItem);
    return location.pathname === link;
  };

  // Get the link for the logo (first menu item's link)
  const getLogoLink = () => {
    if (menuItems.length === 0) return '/';
    return getMenuItemLink(menuItems[0]);
  };


  return (
    <nav className="surface shadow-sm">
      <div className="container mx-auto px-4">
        {/* Main top bar */}
        <div className="flex justify-between items-center h-16">
          {/* Desktop: Logo + Menu Items */}
          <div className="hidden md:flex items-center space-x-6 flex-1">
            {/* Logo */}
            <Link
              to={getLogoLink()}
              className={`text-2xl font-bold logo-text transition-colors ${
                isLogoActive()
                  ? 'text-primary-600 dark:text-primary-400'
                  : 'text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300'
              }`}
            >
              Kwasai
            </Link>

            {/* Menu Items */}
            {menuItems.map((item) => (
              <Link
                key={item.id}
                to={getMenuItemLink(item)}
                onClick={(e) => handleMenuClick(e, item)}
                className={`text-sm transition-colors ${
                  isMenuItemActive(item)
                    ? 'text-primary-600 dark:text-primary-400 font-semibold'
                    : 'text-secondary hover:text-primary-600 dark:hover:text-primary-400'
                }`}
              >
                {item.title}
              </Link>
            ))}
          </div>

          {/* Mobile: Hamburger */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-secondary hover:text-primary transition-colors"
            aria-label="Toggle menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>

          {/* Mobile: Logo (centered) */}
          <Link
            to={getLogoLink()}
            className={`md:hidden text-2xl font-bold logo-text transition-colors absolute left-1/2 transform -translate-x-1/2 ${
              isLogoActive()
                ? 'text-primary-600 dark:text-primary-400'
                : 'text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300'
            }`}
          >
            Kwasai
          </Link>

          {/* Desktop: Right side controls with user info */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated && (
              <Link to={`/profile/${profile?.id}/page/dashboard`} className="hover:opacity-80 transition-opacity">
                <ProfileAvatar
                  profileId={profile?.id}
                  displayName={profile?.display_name || user?.email}
                  roleId={profile?.role_id}
                  size="md"
                  showName={true}
                  showRole={true}
                />
              </Link>
            )}

            {/* Notifications */}
            {isAuthenticated && <Notifications />}

            {isAuthenticated && (
              <button
                onClick={handleLogout}
                className="text-sm text-secondary hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              >
                Logout
              </button>
            )}

            {!isAuthenticated && (
              <Link
                to="/login"
                className="text-sm text-secondary hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              >
                Login
              </Link>
            )}

            {/* Admin mode switch (only for admins) */}
            {isAdmin && (
              <button
                onClick={toggleAdminMode}
                className={`p-2 rounded transition-colors ${
                  isAdminMode
                    ? 'bg-primary-600 text-white'
                    : 'text-secondary hover:text-primary'
                }`}
                title={isAdminMode ? 'Switch to Site Mode' : 'Switch to Admin Mode'}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
            )}

            {/* Role dropdown (only for admins) */}
            {isAdmin && roles.length > 0 && (
              <select
                value={apparentRoleId || profile?.role_id || ''}
                onChange={handleRoleChange}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-primary text-sm"
                title="Test as role"
              >
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.label}
                  </option>
                ))}
              </select>
            )}

            {/* Theme toggle */}
            <button
              onClick={cycleTheme}
              className="text-secondary hover:text-primary transition-colors p-2"
              title={`Theme: ${theme}`}
            >
              {getThemeIcon()}
            </button>
          </div>

          {/* Mobile: Right side controls (theme) */}
          <div className="md:hidden flex items-center space-x-2">
            {/* Theme toggle */}
            <button
              onClick={cycleTheme}
              className="text-secondary hover:text-primary transition-colors p-2"
              title={`Theme: ${theme}`}
            >
              {getThemeIcon()}
            </button>
          </div>
        </div>

        {/* Mobile: User info below top bar */}
        <div className="md:hidden border-t border-gray-200 dark:border-gray-700 py-3">
          {isAuthenticated ? (
            <div className="space-y-3">
              {/* User info and logout */}
              <div className="flex items-center justify-between">
                <Link to={`/profile/${profile?.id}/page/dashboard`} className="hover:opacity-80 transition-opacity">
                  <ProfileAvatar
                    profileId={profile?.id}
                    displayName={profile?.display_name || user?.email}
                    roleId={profile?.role_id}
                    size="md"
                    showName={true}
                    showRole={true}
                  />
                </Link>
                <div className="flex items-center gap-2">
                  <Notifications />
                  <button
                    onClick={handleLogout}
                    className="text-sm text-secondary hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  >
                    Logout
                  </button>
                </div>
              </div>

              {/* Admin controls */}
              {isAdmin && (
                <div className="flex items-center gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                  {/* Admin mode switch */}
                  <button
                    onClick={toggleAdminMode}
                    className={`p-2 rounded transition-colors ${
                      isAdminMode
                        ? 'bg-primary-600 text-white'
                        : 'text-secondary hover:text-primary'
                    }`}
                    title={isAdminMode ? 'Switch to Site Mode' : 'Switch to Admin Mode'}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </button>

                  {/* Role dropdown */}
                  {roles.length > 0 && (
                    <select
                      value={apparentRoleId || profile?.role_id || ''}
                      onChange={handleRoleChange}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-primary text-sm"
                      title="Test as role"
                    >
                      {roles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/login"
              className="text-sm text-secondary hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              Login
            </Link>
          )}
        </div>

        {/* Mobile: Menu overlay */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 dark:border-gray-700 py-2">
            <div className="flex flex-col space-y-1">
              {menuItems.map((item) => (
                <Link
                  key={item.id}
                  to={getMenuItemLink(item)}
                  onClick={(e) => {
                    handleMenuClick(e, item);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`px-4 py-3 text-sm transition-colors rounded ${
                    isMenuItemActive(item)
                      ? 'bg-primary-600 !text-white font-semibold'
                      : 'text-secondary hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400'
                  }`}
                >
                  {item.title}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default TopBar;
